import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import { toMoney } from "../../utils/toMoney";
import { formatDate } from "../../utils/toDate";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import { useEffect, useState } from "react";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/CustomButton";

export const TABLE_CELL_TYPES = Object.freeze({
  LINK: "link",
  TEXT: "text",
  DATE: "date",
  MONEY: "money",
  NOTES: "notes",
  ELEMENT: "element",
  ARROW: "arrow",
});

export default function RoundedTableWithNesting({
  titles,
  rows,
  fullData,
  selectedRows,
  setSelectedRows,
  withCheckbox,
  pagination,
  setPagination,
  onSelectAll = null,
}) {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [selectAll, setSelectAll] = useState(false);
  // State to track expanded rows
  const [expandedRows, setExpandedRows] = useState({});

  const handleSelectAll = async () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      let allCustomers = [];

      if (onSelectAll) {
        allCustomers = await onSelectAll();
      } else {
        allCustomers = fullData;
      }

      const dataWithId = allCustomers.map((item) => ({
        id: item.id,
        data: item,
      }));

      setSelectedRows(dataWithId);
    }
  };

  useEffect(() => {
    const allSelected =
      fullData?.length > 0 &&
      fullData.every((item) =>
        selectedRows?.some((selected) => selected.id === item.id)
      );
    setSelectAll(allSelected);
  }, [selectedRows, fullData]);

  const handleSelectOne = (id) => {
    const rowData = { id, data: fullData.find((item) => item.id === id) };

    setSelectedRows((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === id);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== id);
      } else {
        return [...prevSelected, rowData];
      }
    });
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const renderTableCell = (row, rowIndex, hasSubcategories = false) => {
    return row?.map((cell, index, array) => {
      const value = cell.value || "-";
      const sign = cell?.sign || "";
      const currency = cell?.currency || "";
      const bold = cell?.bold ? "t-bold_bold" : "";
      const firstCell =
        !withCheckbox && index === 0 ? "the-rounded-first-cell" : "";
      const lastCell =
        index === array.length - 1 ? "the-rounded-last-cell" : "";
      const roundedCorners = `${firstCell} ${lastCell}`;
      const noWrapText = cell.noWrapText ? "" : "t-eclipse-text";
      const keepOnMobile =
        index > 0 ? (cell.keepOnMobile ? "" : "mobile-hider") : "";

      // If this is the first cell and the row has subcategories, add an arrow
      if (index === 0 && hasSubcategories) {
        return (
          <td
            className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
            key={index}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                onClick={() => toggleRowExpansion(rowIndex)}
                style={{
                  cursor: "pointer",
                  transform: expandedRows[rowIndex]
                    ? "rotate(-90deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                <img src={previousIcon} style={{ height: "15px" }} />
              </div>
              {cell.type === TABLE_CELL_TYPES.TEXT ? (
                <p className={noWrapText}>{value}</p>
              ) : cell.type === TABLE_CELL_TYPES.ELEMENT ? (
                value
              ) : (
                <div className={noWrapText}>{value}</div>
              )}
            </div>
          </td>
        );
      }

      switch (cell.type) {
        case TABLE_CELL_TYPES.LINK:
          return (
            <td
              className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <Link to={cell?.to} className={`link ${noWrapText}`}>
                {value}
              </Link>
            </td>
          );
        case TABLE_CELL_TYPES.TEXT:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <p className={noWrapText}>{value}</p>
            </td>
          );
        case TABLE_CELL_TYPES.MONEY:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {toMoney(value) ? `${toMoney(value)} ${currency}` : "-"}
            </td>
          );
        case TABLE_CELL_TYPES.DATE:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value ? formatDate(value) : "-"}
            </td>
          );
        case TABLE_CELL_TYPES.NOTES:
          return (
            <td
              className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <p className={`notes-cell ${noWrapText}`}>{value}</p>
            </td>
          );
        case TABLE_CELL_TYPES.ELEMENT:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value}
            </td>
          );
        default:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <div className={noWrapText}>{value}</div>
            </td>
          );
      }
    });
  };

  return (
    <>
      <section className="rounded-table-container">
        <table className="i-table">
          <thead className="table-head">
            <tr>
              {withCheckbox && (
                <th className="bold-text">
                  <CustomCheckbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </th>
              )}
              {titles?.map((column, index) =>
                index > 0 ? (
                  <th
                    key={index}
                    className={`cell-title ${
                      column?.keepOnMobile ? "" : "mobile-hider"
                    }`}
                  >
                    {t(column?.value)}
                  </th>
                ) : (
                  <th key={index} className={`bold-text cell-title ${column}`}>
                    {t(column?.value)}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="table-body">
            {rows?.map((row, index) => {
              // Check if this row has subcategories (defined by the parent component)
              const hasSubcategories = row.hasSubcategories;
              const subcategories = row.subcategories || [];

              return (
                <>
                  <tr key={`row-${index}`}>
                    {withCheckbox && (
                      <td className={`cell the-rounded-first-cell`}>
                        <CustomCheckbox
                          checked={selectedRows?.some(
                            (item) => item.data.id === fullData[index].id
                          )}
                          onChange={() =>
                            handleSelectOne(fullData[index].id, row)
                          }
                        />
                      </td>
                    )}
                    {renderTableCell(row.data || row, index, hasSubcategories)}
                  </tr>

                  {/* Render subcategories if expanded */}
                  {hasSubcategories &&
                    expandedRows[index] &&
                    subcategories.map((subItem, subIndex) => (
                      <tr
                        key={`subrow-${index}-${subIndex}`}
                        className="subcategory-row"
                      >
                        {withCheckbox && <td className="cell"></td>}
                        {/* Add indentation for first cell */}
                        <td
                          className={`cell ${
                            !withCheckbox ? "the-rounded-first-cell" : ""
                          }`}
                          //         style={{ paddingLeft: "30px" }}
                        >
                          <p className="t-eclipse-text">{subItem.name}</p>
                        </td>
                        <td className="cell">{`${toMoney(subItem.price)} ${
                          subItem?.currency
                        }`}</td>
                        <td className="cell the-rounded-last-cell">
                          {subItem.options || "-"}
                        </td>
                      </tr>
                    ))}
                </>
              );
            })}
          </tbody>
        </table>
      </section>
      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }))
              }
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }))
              }
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}
    </>
  );
}
